import { loadScript, pushTrack } from "utils";
import type { InstantConfigRepository } from "instant-config";
import { LoadTimeEvent } from "common-types";
import { AdEnginePlugin, type Hooks } from "../../../../core/AdEnginePlugin";
import type { GptWrapper } from "../../../../core/GptWrapper";

/**
 * BrandMetricsPlugin class integrates Brand Metrics with the AdEngine.
 * The plugin will load only after the first ad is rendered on the page.
 * The plugin loads a script from BrandMetrics on the page.
 *
 * Implementation documentation can be found in the original tickets:
 * @see {@link https://fandom.atlassian.net/browse/ADEN-13416}
 * @see {@link https://fandom.atlassian.net/browse/ADEN-12373}
 */
export class BrandMetricsPlugin extends AdEnginePlugin {
  private readonly BRAND_METRICS_LIB_URL =
    "//cdn.brandmetrics.com/tag/9097a5369e204e6eac53b45c7dde13c5/fandom.com_au.js";
  // This variable ensures that the script will only load once.
  // It's a safeguard to ensure that when the listener fires twice, and before it is removed,
  // the Brand Metrics script will not be loaded in twice.
  private firstAdRendered = false;

  /**
   * Loads the Brand Metrics script.
   */
  private async loadBrandMetricsScript(): Promise<void> {
    try {
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "brand-metrics_started",
        time: performance.now(),
      });
      await loadScript(this.BRAND_METRICS_LIB_URL);
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "brand-metrics_loaded",
        time: performance.now(),
      });
    } catch (error) {
      console.error("Failed to load Brand Metrics script.", error);
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "brand-metrics_error_loading",
        time: performance.now(),
      });
    }
  }

  /**
   * Checks if the Brand Metrics plugin is enabled.
   * @returns {boolean} True if enabled, false otherwise.
   */
  static isEnabled(config: InstantConfigRepository): boolean {
    return Boolean(config?.icBrandMetrics);
  }

  /**
   * Initializes the Brand Metrics plugin.
   * This should only load after the hook is called in this plugin.
   */
  private init(gpt: GptWrapper): void {
    const slotRenderEndedListener = () => {
      if (!this.firstAdRendered) {
        this.firstAdRendered = true;
        gpt.removeEventListener("slotRenderEnded", slotRenderEndedListener);
        this.loadBrandMetricsScript();
      }
    };

    gpt.addEventListener("slotRenderEnded", slotRenderEndedListener);
  }

  hooks: Hooks = {
    /**
     * Hook that runs after the GPT wrapper is initialized.
     * @param {GptWrapper} gpt - The GPT wrapper instance.
     */
    onAfterGptWrapperInit: (gpt: GptWrapper): void => {
      this.init(gpt);
    },
  };
}
